<template>
  <div class="desktop-menu">
    <ul
      class="desktop-menu__list"
      @[isWholeMenuLinked&&`mouseleave`]="hideOverlay"
      @[isWholeMenuLinked&&`mouseenter`]="showOverlay"
    >
      <li
        v-for="(link, index) in mainMenu"
        :key="index"
        ref="desktopMenuListItems"
        class="desktop-menu__list-item"
        :class="{ 'is-relative': getRelativePosition(link.title) }"
        :data-shopify-editor-block="getShopifyAttributes(link.title)"
        @[!isWholeMenuLinked&&`mouseenter`]="openMegaMenu(link.title)"
        @[!isWholeMenuLinked&&`mouseleave`]="hideOverlay"
      >
        <a
          :href="link.url"
          class="desktop-menu__link"
          :aria-label="link.image ? link.title : null"
        >
          <div
            v-if="link.image"
            class="desktop-menu__linked-image"
            v-html="link?.image?.content"
          />
          <span
            v-else
            :class="{
              'has-badge': link.badge,
            }"
          >
            <span
              class="desktop-menu__link-title"
              :class="{
                'highlighted': link.isHighlighted,
                'highlighted--bg': link.isHighlighted && enableLinkBackground,
              }"
              v-text="link.title"
            />
            <badge
              v-if="link.badge"
              class="desktop-menu__badge"
              :label="link.badge.label"
              :background-colour="link.badge.backgroundColour"
              :text-colour="link.badge.textColour"
            />
          </span>
        </a>
        <mega-menu
          v-if="enableMegamenu"
          class="desktop-menu__megamenu"
          :linked-block="getLinkedBlock(link.title)"
          :link="link"
        />
      </li>
    </ul>

    <overlay
      v-if="enableMegamenu"
      type="popup"
      parent-class="desktop-menu__overlay"
      :is-active="isOverlayActive"
      @close-overlay="hideOverlay"
    />
  </div>
</template>

<script>

/**
 * Component: Desktop Menu
 * -----------------------------------------------------------------------------
 * @param {object}  [mainMenu] - Formatted nested linklist.
 * @param {boolean} [enableMegamenu] - Setting for enabling megamenu.
 * @param {boolean} [enableLinkBackground] - Setting for enabling background styling for highlighted links.
 * @param {Array}   [sectionBlocks] - Formatted section blocks array.
 */

import classes from '~/helpers/classes'
import { shopifyAttributesToJSON } from '~/helpers/strings'

import { defineAsyncComponent } from 'vue'
import { nextTick } from 'vue'

import Overlay from '~components/overlay/overlay'
import Badge from '~components/badge/badge'

export default {
  name: 'DesktopMenu',

  components: {
    Overlay,
    Badge,
    MegaMenu: defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.mega-menu' */
          '~components/site-header-components/mega-menu/mega-menu'
        ),
    }),
  },

  props: {
    mainMenu: {
      type: Array,
      default: () => [],
    },
    enableLinkBackground: {
      type: Boolean,
      default: false,
    },
    sectionBlocks: {
      type: Array,
      default: () => [],
    },
    enableMegamenu: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isOverlayActive: false,
    }
  },

  computed: {

    /**
     * Calculate if there is a corresponding mega menu block for each menu item.
     * - this uses for conditional events binding
     * - this is done so that the overlay background does not blink when the
     * hover is moved from one link element to another
     * - the blinking will still occur if at least one item does not have a mega
     * menu, because then mouse events which open the overlay will be listened
     * on each individual link
     * @returns {boolean}
     */
    isWholeMenuLinked() {
      if (!this.sectionBlocks?.length) {
        return false
      }

      const allItemsLinked = Boolean(
        !this.mainMenu.some((link) => !this.getLinkedBlock(link.title)),
      )

      return allItemsLinked
    },

    /**
     * Checks if the device supports touch events.
     * @returns {boolean}
     */
    isTouch() {
      return (
        'ontouchstart' in window ||
          (window.DocumentTouch && document instanceof window.DocumentTouch) ||
          navigator.maxTouchPoints > 0 ||
          window.navigator.msMaxTouchPoints > 0
      )
    },
  },

  mounted() {
    this.setEventListeners()
  },

  methods: {

    /**
     * Find the corresponding menu block on the given link.
     * @param {string} linkTitle - link title  to which the section block belongs
     * @returns {object | boolean} - section block object.
     */
    getLinkedBlock(linkTitle) {
      const megamenuBlock = this.sectionBlocks.find((block) => {
        return (
          block.heading?.toLowerCase().trim() ===
            linkTitle?.toLowerCase().trim()
        )
      })

      return megamenuBlock || false
    },

    /**
     * Show the 2nd level desktop megamenu for specific link.
     * - if no mega-menu blocks are found, then don't display the overlay
     * @param {string} linkTitle - link title.
     */
    openMegaMenu(linkTitle) {
      const megamenuBlock = this.getLinkedBlock(linkTitle)

      if (!megamenuBlock) {
        return
      }

      this.showOverlay()
    },

    /**
     * Show the overlay.
     */
    showOverlay() {
      mint.EventBus.dispatch('cartDrawer:close')
      this.isOverlayActive = true

      if (!this.isTouch) {
        return
      }

      /**
       * - Fix exclusively for iPads/tablets with >= 1024px resolution
       * - Since the first click on menu item behaves like hover, there could be
       * a state, when the hover is already removed and overlay is stuck
       * - That happens because `mouseleave` doesn't work on touch devices, and
       * `mouseout` works inconsistently
       * - So we should unlock the body and hide overlay when the user attempts
       * to scroll beyond the menu
       */
      nextTick(() => {
        setTimeout(() => {
          document.body.classList.remove(classes.locked)
        }, 0)
      })
    },

    /**
     * Close the overlay.
     */
    hideOverlay() {
      this.isOverlayActive = false
    },

    /**
     * Determines whether a link should have a `relative` position in order for
     * small type megamenus to be displayed under a particular link.
     * @param {string} linkTitle - link title to find the corresponding menu block
     * @returns {boolean} - section block object.
     */
    getRelativePosition(linkTitle) {
      const megamenuBlock = this.getLinkedBlock(linkTitle)

      if (!megamenuBlock) {
        return false
      }

      return (
        megamenuBlock.type === 'style_2' || megamenuBlock.type === 'style_3'
      )
    },

    /**
     * Calculate `block.shopify_attributes` for given link.
     * @param linkTitle - link title.
     * @returns {string|null}
     */
    getShopifyAttributes(linkTitle) {
      const megamenuBlock = this.getLinkedBlock(linkTitle)

      if (!megamenuBlock) {
        return null
      }

      const formattedShopifyAttributes = shopifyAttributesToJSON(
        megamenuBlock.shopify_attributes,
      )

      return formattedShopifyAttributes?.editorBlock || null
    },

    /**
     * Activates a mega-menu when its block has been selected within Shopify Customise.
     * @param {object} payload - Mint EventBus payload containing Shopify event data.
     * @param {string} payload.blockId - The Shopify block ID.
     * @param {boolean} payload.isSelected - Whether the block has been selected or
     * deselected.
     * @param {HTMLElement} payload.target - The target HTML element.
     */
    handleBlockSelect({ blockId, isSelected = false, target }) {
      this.$refs.desktopMenuListItems.forEach((item) => {
        item.classList.remove(classes.active)
      })

      this.hideOverlay()

      if (!blockId || !isSelected) {
        return
      }

      const activeListItem = target

      if (!activeListItem) {
        return
      }

      activeListItem.classList.add(classes.active)
      this.showOverlay()
    },

    /**
     * Set Event Listeners.
     */
    setEventListeners() {
      if (Shopify?.designMode || mint.request?.designMode) {

        // Listen to unified Mint EventBus customiser events
        mint.EventBus.on('mint:customiser-event', (payload) => {
          if (payload.type?.includes('block')) {
            this.handleBlockSelect(payload)
          }

          if (payload.type?.includes('section')) {
            this.handleSectionSelect(payload)
          }
        })
      }

      if (!this.isTouch) {
        return
      }

      /**
       * Hide the overlay the touch goes beyond the menu
       * - `mouseleave` doesn't work for touch devices
       * - the other events like the `touchstart` or `touchmove` doesn't work as
       * expected either
       * - if the overlay is added not as a separate element within the <body>
       * but with an `::after`, the hover can also get stuck and not get through
       * - so we unlock the scroll on iPads/tablets within the `showOverlay`
       * method
       */
      window.addEventListener('scroll', this.hideOverlay, { passive: true })
    },

    /**
     * Handle section-level selection events.
     * @param {object} payload - Mint EventBus payload containing Shopify event data.
     * @param {boolean} payload.isSelected - Whether the section has been selected or
     * deselected.
     */
    handleSectionSelect({ isSelected = false }) {
      if (isSelected) {
        return
      }

      // Close mega menu when section is deselected
      this.hideOverlay()
      this.$refs.desktopMenuListItems.forEach((item) => {
        item.classList.remove(classes.active)
      })
    },
  },
}
</script>

<style src="./desktop-menu.scss" lang="scss"></style>
